import type { CSSProperties, HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { cx } from "../cx";
import { Avatar } from "../primitives/Avatar";

/** Tabla del sistema, siempre dentro de su wrap (scroll horizontal contenido). `dense`: ritmo compacto (registros largos, una línea por fila — auditoria.html). */
export function Table({ stale, busy, label, dense, className, style, wrapRef, children }: { stale?: boolean | undefined; busy?: boolean | undefined; label?: string | undefined; dense?: boolean | undefined; className?: string | undefined; style?: CSSProperties | undefined; wrapRef?: React.Ref<HTMLDivElement> | undefined; children: ReactNode }) {
  return (
    <div ref={wrapRef} className={cx("sk-tablewrap", stale && "sk-stale", className)} aria-busy={busy || undefined} aria-label={label} style={style}>
      <table className={cx("sk-table", dense && "sk-table--dense")}>{children}</table>
    </div>
  );
}
export type SortDir = "ascending" | "descending";
/** Cabecera; `sortable` pinta la flecha y emite aria-sort (la ordenación la lleva la página). */
/** `tip`: el globo del sistema que explica la columna («Preguntas pendientes de contestar», biblioteca.html). */
export function Th({ sortable, sorted, num, tip, children, onSort, ...rest }: { sortable?: boolean | undefined; sorted?: SortDir | null | undefined; num?: boolean | undefined; tip?: string | undefined; onSort?: (() => void) | undefined; children?: ReactNode } & ThHTMLAttributes<HTMLTableCellElement>) {
  if (!sortable) return <th className={num ? "sk-num-cell" : undefined} data-sk-tip={tip} {...rest}>{children}</th>;
  return (
    <th
      className={cx(num && "sk-num-cell", "sk-sortable")}
      tabIndex={0}
      aria-sort={sorted ?? undefined}
      data-sk-tip={tip}
      onClick={onSort}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSort?.(); } }}
      {...rest}
    >
      {children} <span className="sk-sort">{sorted === "descending" ? "↓" : sorted === "ascending" ? "↑" : "↕"}</span>
    </th>
  );
}
export function TdNum({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cx("sk-num-cell", className)} {...rest} />;
}
/**
 * Celda título + sublínea (slug en mono la pone la página con <Mono> si toca).
 * `tag`: lo que sigue al título en su línea (el código del cuaderno).
 * `hyphens`: el título se parte con guion (`sk-cell-title--hyphens`): títulos
 * REALES largos («…internacionalización y adquisiciones», biblioteca.html)
 * que, sin partir, ensanchan la columna y sacan la tabla de su caja.
 */
export function CellTitle({ title, tag, sub, hyphens }: { title: ReactNode; tag?: ReactNode | undefined; sub?: ReactNode | undefined; hyphens?: boolean | undefined }) {
  return <td className={hyphens === true ? "sk-cell-title--hyphens" : undefined}><b>{title}</b>{tag !== undefined && <> {tag}</>}{sub !== undefined && <span className="sk-sub">{sub}</span>}</td>;
}
/** Celda persona: avatar + nombre + sublínea (equipo.html). */
export function CellPerson({ avatar, name, sub }: { avatar: ReactNode; name: ReactNode; sub?: ReactNode | undefined }) {
  return (
    <td style={{ display: "flex", alignItems: "center", gap: "var(--sk-sp-3)" }}>
      <Avatar>{avatar}</Avatar>
      <span><b>{name}</b>{sub !== undefined && <span className="sk-sub">{sub}</span>}</span>
    </td>
  );
}
/** Acciones por fila — SIEMPRE en tier quieto (decisión F6-1); la página pasa los botones. */
export function RowActions({ children }: { children: ReactNode }) {
  return <td><div className="sk-rowactions">{children}</div></td>;
}
/** Delta con semántica: good/bad (▲ rojo = métrica en mal estado). */
export function Delta({ tone, children }: { tone: "good" | "bad"; children: ReactNode }) {
  return <span className={`sk-delta sk-delta--${tone}`}>{children}</span>;
}
/** Grupo de badges en una celda. */
export function BadgeGroup({ children, ...rest }: { children: ReactNode } & HTMLAttributes<HTMLSpanElement>) {
  return <span className="sk-badges" {...rest}>{children}</span>;
}
export function Pagination({ children, meta }: { children: ReactNode; meta?: ReactNode | undefined }) {
  return <div className="sk-pagination">{children}{meta !== undefined && <span className="sk-page-meta">{meta}</span>}</div>;
}
export function PageBtn({ current, children, ...rest }: { current?: boolean | undefined; children: ReactNode } & HTMLAttributes<HTMLButtonElement> & { disabled?: boolean | undefined }) {
  return <button type="button" className="sk-page" aria-current={current ? "page" : undefined} {...rest}>{children}</button>;
}
/** Hueco de paginación cuando hay más páginas de las que caben (>7, decisión F6-conocimiento). */
export function PageEllipsis() {
  return <span className="sk-page-ellipsis" aria-hidden="true">…</span>;
}
